if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mediaList: this.querySelector('.product__media-list'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // Load + autoplay the active media on page load. If the first media is a
        // video it starts playing immediately (muted, no controls). Tapping the
        // video toggles play/pause (see DeferredMedia.loadContent).
        this.playActiveMedia(this.elements.viewer.querySelector('.product__media-item.is-active'));
        this.syncGalleryHeight = this.syncGalleryHeight.bind(this);
        this.syncGalleryHeight();
        this.mql.addEventListener('change', this.syncGalleryHeight);
        // The constructor runs at `defer` time — before the active video's
        // metadata, the poster image, web fonts and (on iOS) the final 100dvh
        // have settled, so this first measurement can come out too tall. Nothing
        // else re-syncs the mobile gallery height until the shopper changes
        // slide, so a wrong first value would otherwise stick and leave a gap
        // between the media and the thumbnail strip. Re-measure once it settles.
        window.addEventListener('load', this.syncGalleryHeight);
        window.addEventListener('resize', debounce(this.syncGalleryHeight, 150));

        // Keep the desktop "fit the gallery above the fold" constraint
        // (.product-media-container.constrain-height in section-main-product.css)
        // driven by real measured space instead of hand-guessed reserves.
        this.updateViewportOffset = this.updateViewportOffset.bind(this);
        this.updateViewportOffset();
        this.mql.addEventListener('change', this.updateViewportOffset);
        window.addEventListener('resize', debounce(this.updateViewportOffset, 150));
        window.addEventListener('load', this.updateViewportOffset);

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 250));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeSlide = event.detail.currentElement;
        // A swipe on the mobile main slider fires slideChanged but not the
        // thumbnail-click path, so nothing else moves `is-active`. Promote the
        // swiped-to slide here so its <deferred-media> becomes visible on mobile
        // (.is-active .deferred-media { display: block }) and autoplays, and the
        // slide we left gets paused. This mirrors a thumbnail click without the
        // extra scrolling setActiveMedia() does.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeSlide.classList.add('is-active');

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeSlide.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(activeSlide);
        this.syncGalleryHeight();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncGalleryHeight();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        deferredMedia.loadContent(false);
        // loadContent() only builds + plays the media the first time it runs
        // (it early-returns once `loaded` is set). When the shopper navigates
        // away and back, re-trigger autoplay on the already-loaded media —
        // unless they paused it themselves, in which case leave it paused and
        // keep the manual play affordance visible so they can resume it.
        const video = deferredMedia.querySelector('video');
        if (video) {
          if (deferredMedia.userPaused) {
            deferredMedia.classList.add('deferred-media--show-play');
            return;
          }
          video.muted = true;
          video.playsInline = true;
          // Optimistically hide the manual play sign (a stale one may linger
          // from the pause that fired when we navigated away); re-show it only
          // if this play attempt is actually rejected.
          deferredMedia.classList.remove('deferred-media--show-play');
          video.play().catch(() => deferredMedia.classList.add('deferred-media--show-play'));
          return;
        }
        const youtube = deferredMedia.querySelector('.js-youtube');
        if (youtube) {
          youtube.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          return;
        }
        const vimeo = deferredMedia.querySelector('.js-vimeo');
        if (vimeo) {
          vimeo.contentWindow.postMessage('{"method":"play"}', '*');
        }
      }

      syncGalleryHeight() {
        if (!this.elements.mediaList) return;
        if (this.mql.matches) {
          this.elements.mediaList.style.removeProperty('height');
          return;
        }
        const activeItem = this.elements.mediaList.querySelector('.product__media-item.is-active');
        const activeContent = activeItem && activeItem.querySelector('.product-media-container');
        if (!activeItem || !activeContent) return;

        // Re-sync when the real media finally reports its size — the ratio box
        // below is often measured before a video's metadata or the poster image
        // has loaded. Bind once per media element.
        activeContent.querySelectorAll('video, model-viewer, iframe, img').forEach((el) => {
          if (el.dataset.syncHeightBound) return;
          el.dataset.syncHeightBound = 'true';
          ['loadedmetadata', 'canplay', 'load'].forEach((evt) => el.addEventListener(evt, this.syncGalleryHeight));
        });

        const itemStyle = window.getComputedStyle(activeItem);
        const verticalPadding = parseFloat(itemStyle.paddingTop) + parseFloat(itemStyle.paddingBottom);

        const contentRect = activeContent.getBoundingClientRect();
        let contentHeight = contentRect.height;

        // The .product-media-container is a fixed-ratio box. When media_fit is
        // "contain" and the box ratio doesn't match the media (e.g. Shopify never
        // reported media.aspect_ratio, so it fell back to 1:1), the media paints
        // letterboxed inside it — shrink to the height the media actually fills
        // so there's no dead space between it and the thumbnail strip.
        const video = activeContent.querySelector('video');
        if (
          activeContent.classList.contains('media-fit-contain') &&
          video &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          const fitted = contentRect.width * (video.videoHeight / video.videoWidth);
          if (fitted > 0) contentHeight = Math.min(contentHeight, fitted);
        }

        this.elements.mediaList.style.height = `${contentHeight + verticalPadding}px`;
      }

      updateViewportOffset() {
        if (!this.mql.matches) {
          this.style.removeProperty('--gallery-viewport-offset');
          return;
        }
        // Absolute document offset of the gallery top — scroll-independent, and
        // automatically accounts for the announcement bar, header, section
        // padding, and any Shopify rollout banner stacked above it.
        const galleryTop = this.getBoundingClientRect().top + window.scrollY;
        const thumbs = this.elements.thumbnails;
        // Only count the thumbnail strip when it sits below the image (a
        // horizontal row). In the "thumbnails on the left" layout it's a
        // vertical column beside the image and doesn't eat vertical space.
        const thumbsIsRow = thumbs && !this.classList.contains('media-gallery--thumbnails-left');
        const thumbsHeight = thumbsIsRow ? thumbs.getBoundingClientRect().height : 0;
        // + small buffer so the thumbnail strip clears the fold rather than
        // sitting flush against it.
        const offset = Math.round(galleryTop + thumbsHeight + 16);
        this.style.setProperty('--gallery-viewport-offset', `${offset}px`);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
